import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, Briefcase, ArrowRight, Loader2, User } from 'lucide-react'
import api from '@/shared/lib/api'
import { useAuthStore } from '@/core/stores/auth'

export function SetupOrganizationPage() {
  const navigate = useNavigate()
  const { user, validateSession } = useAuthStore()

  const [organizationName, setOrganizationName] = useState('')
  const [workspaceName, setWorkspaceName] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const canSubmit = organizationName.trim() && workspaceName.trim() && jobTitle.trim()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    setLoading(true)
    setError('')

    try {
      await api.post('/onboarding/setup', {
        organization_name: organizationName.trim(),
        workspace_name: workspaceName.trim(),
        job_title: jobTitle.trim(),
      })
      // Refresh user profile to get onboarding_completed=true
      await validateSession()
      navigate('/home', { replace: true })
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de la configuration')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gold-light flex items-center justify-center">
            <svg width="28" height="28" viewBox="0 0 44 44" fill="none">
              <circle cx="22" cy="22" r="8" stroke="#C9A84C" strokeWidth="2" fill="none"/>
              <circle cx="22" cy="22" r="14" stroke="#C9A84C" strokeWidth="1.2" opacity="0.4" fill="none"/>
              <circle cx="22" cy="22" r="20" stroke="#C9A84C" strokeWidth="0.8" opacity="0.2" fill="none"/>
              <circle cx="22" cy="22" r="3" fill="#C9A84C"/>
            </svg>
          </div>
          <h1 className="font-serif text-2xl font-bold text-ink tracking-tight">Configurez votre espace</h1>
          <p className="text-sm text-ink-muted mt-2">
            Bienvenue{user?.full_name ? `, ${user.full_name}` : ''} ! Quelques informations pour personnaliser votre expérience.
          </p>
        </div>

        {/* User info badge */}
        {user && (
          <div className="flex items-center justify-center mb-6">
            <div className="inline-flex items-center gap-3 bg-white border border-ink-border rounded-full px-4 py-2">
              {user.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-6 h-6 rounded-full" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-gold-light flex items-center justify-center text-[10px] font-bold text-brick">
                  {(user.full_name || user.email)[0].toUpperCase()}
                </div>
              )}
              <span className="text-sm text-ink font-medium">{user.email}</span>
              <span className="text-[10px] text-paper bg-onyx px-2 py-0.5 rounded-full font-medium">Owner</span>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-ink-border shadow-card p-6 space-y-5">
          {/* Organization name */}
          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5">Nom de votre organisation</label>
            <div className="relative">
              <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint" />
              <input type="text" value={organizationName} onChange={(e) => setOrganizationName(e.target.value)}
                placeholder="Mon Agence Marketing, Ma Société, ..."
                required
                className="w-full pl-10 pr-4 py-2.5 border border-ink-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
            </div>
            <p className="text-[11px] text-ink-faint mt-1">Votre entreprise ou agence. Regroupe vos workspaces.</p>
          </div>

          {/* Workspace name */}
          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5">Nom du workspace</label>
            <div className="relative">
              <Briefcase size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint" />
              <input type="text" value={workspaceName} onChange={(e) => setWorkspaceName(e.target.value)}
                placeholder="Client Premium, Projet X, Mon Entreprise, ..."
                required
                className="w-full pl-10 pr-4 py-2.5 border border-ink-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
            </div>
            <p className="text-[11px] text-ink-faint mt-1">Un workspace = un environnement de travail avec ses propres données et modules.</p>
          </div>

          {/* Job title */}
          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5">Votre poste</label>
            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint" />
              <input type="text" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)}
                placeholder="CEO, Directeur Marketing, Chef de projet, ..."
                required
                className="w-full pl-10 pr-4 py-2.5 border border-ink-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Submit */}
          <button type="submit" disabled={loading || !canSubmit}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-onyx text-paper rounded-lg text-sm font-semibold hover:bg-onyx-light transition-colors disabled:opacity-50">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
            {loading ? 'Configuration en cours...' : 'Accéder à TremorApp'}
          </button>

          <p className="text-[11px] text-ink-faint text-center">
            Vous pourrez modifier ces informations à tout moment dans les paramètres.
          </p>
        </form>
      </div>
    </div>
  )
}
